import { datadogRum } from '@datadog/browser-rum';

function isDev() {
  return (
    (typeof import.meta !== 'undefined' &&
      (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true) ||
    (typeof process !== 'undefined' && process.env.NODE_ENV === 'development')
  );
}

function logDev(actionName: string, context: Record<string, unknown>) {
  if (isDev()) {
    console.log('🐶 [Tracking]', actionName, context);
  }
}

export type FeatureTrackingContext = {
  feature: string;
  step?: string;
  step_index?: number;
  total_steps?: number;
  character_id?: string;
  duration_ms?: number;
  error_message?: string;
  metadata?: Record<string, string | number | boolean>;
};

export type CharacterType = 'custom' | 'free';
export type CharacterTypeInUseSource = 'character_panel';

export const CHARACTER_TYPE_IN_USE_SOURCES: Record<
  'CHARACTER_PANEL',
  CharacterTypeInUseSource
> = {
  CHARACTER_PANEL: 'character_panel',
};

const featureStartTimes = new Map<string, number>();

function getKey(feature: string, entityId?: string): string {
  return entityId ? `${feature}:${entityId}` : feature;
}

function getDuration(feature: string, entityId?: string): number | undefined {
  const key = getKey(feature, entityId);
  const startTime = featureStartTimes.get(key);
  if (startTime == null) return undefined;
  return Date.now() - startTime;
}

export function trackFeatureStarted(
  feature: string,
  context?: Omit<FeatureTrackingContext, 'feature'>,
) {
  const key = getKey(feature, context?.character_id);
  featureStartTimes.set(key, Date.now());
  const payload = { feature, ...context };
  logDev(`${feature}.started`, payload);
  datadogRum.addAction(`${feature}.started`, payload);
}

export function trackFeatureStepCompleted(
  feature: string,
  step: string,
  context?: Omit<FeatureTrackingContext, 'feature' | 'step'>,
) {
  const duration_ms = getDuration(feature, context?.character_id);
  const payload = {
    feature,
    step,
    duration_ms,
    ...context,
  };
  logDev(`${feature}.step_completed`, payload);
  datadogRum.addAction(`${feature}.step_completed`, payload);
}

export function trackFeatureCompleted(
  feature: string,
  context?: Omit<FeatureTrackingContext, 'feature'>,
) {
  const key = getKey(feature, context?.character_id);
  const duration_ms = getDuration(feature, context?.character_id);
  const payload = { feature, duration_ms, ...context };
  logDev(`${feature}.completed`, payload);
  datadogRum.addAction(`${feature}.completed`, payload);
  featureStartTimes.delete(key);
}

export function trackFeatureFailed(
  feature: string,
  context?: Omit<FeatureTrackingContext, 'feature'>,
) {
  const key = getKey(feature, context?.character_id);
  const duration_ms = getDuration(feature, context?.character_id);
  const payload = { feature, duration_ms, ...context };
  logDev(`${feature}.failed`, payload);
  datadogRum.addAction(`${feature}.failed`, payload);
  featureStartTimes.delete(key);
}

export function trackFeatureAbandoned(
  feature: string,
  context?: Omit<FeatureTrackingContext, 'feature'>,
) {
  const key = getKey(feature, context?.character_id);
  const duration_ms = getDuration(feature, context?.character_id);
  const payload = { feature, duration_ms, ...context };
  logDev(`${feature}.abandoned`, payload);
  datadogRum.addAction(`${feature}.abandoned`, payload);
  featureStartTimes.delete(key);
}

export function trackCharacterTypeInUse(context: {
  character_id: string;
  character_type: CharacterType;
  source: CharacterTypeInUseSource;
}) {
  const payload = {
    feature: TRACKED_FEATURES.CHARACTER_UPLOAD,
    metadata: {
      source: context.source,
    },
    ...context,
  };
  logDev('character_upload.character_type_in_use', payload);
  datadogRum.addAction('character_upload.character_type_in_use', payload);
}

export const TRACKED_FEATURES = {
  CHARACTER_UPLOAD: 'character_upload',
  CHARACTER_UPLOAD_RIGGING: 'character_upload_rigging',
  CHARACTER_UPLOAD_RETARGETING: 'character_upload_retargeting',
} as const;
